/**
 * Per-rule applicability evaluation result. Plan §26.10.
 *
 * Kaiyang Lesson §25.4: applicability "unknown" lives at the CONFIG FIELD level,
 * never at the rule level alone. A rule marked `unknown_needs_config` MUST list
 * the specific config field paths that resolve it. Symmetrically,
 * `hidden_by_config` MUST list the fields that suppressed it.
 */

import { z } from "zod";
import { ApplicabilityCategory } from "./primitives";

export const ApplicabilityResultSchema = z
  .object({
    ruleId: z.string().min(1),
    applicabilityCategory: ApplicabilityCategory,
    matchedConfigFields: z.array(z.string()),
    hiddenByConfigFields: z.array(z.string()).optional(),
    needsConfigFields: z.array(z.string()).optional(),
    evaluatedAt: z.string(),
    evaluatorVersion: z.string(),
  })
  .superRefine((r, ctx) => {
    if (
      r.applicabilityCategory === "unknown_needs_config" &&
      (!r.needsConfigFields || r.needsConfigFields.length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["needsConfigFields"],
        message:
          "unknown_needs_config MUST list at least one config field path",
      });
    }
    if (
      r.applicabilityCategory === "hidden_by_config" &&
      (!r.hiddenByConfigFields || r.hiddenByConfigFields.length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["hiddenByConfigFields"],
        message:
          "hidden_by_config MUST list at least one config field path",
      });
    }
  });

export type ApplicabilityResult = z.infer<typeof ApplicabilityResultSchema>;
